import {KnowledgeRagTopic} from 'common/src/modules/ai/ai.enums';
import {ZodTypeAny} from 'zod';
import {ToolName} from '../../copilot.enums';
import {AgentBase, AgentContext, Tool} from '../../copilot.types';
import {OpenAIModelConfig} from '../../openAI/openAI.models';
import askKnowledgeRAGTool from '../../tools/askKnowledgeRAGTool/askKnowledgeRAGTool';

interface AgentBaseCreatorParams<T extends ZodTypeAny> {
  name: string;
  role: string;
  instructions?: string;
  knowledgeTopics?: KnowledgeRagTopic[];
  tools?: Tool<ZodTypeAny>[];
  model: OpenAIModelConfig;
  classificationModel?: OpenAIModelConfig;
  responseSchema: T;
  contexts?: AgentContext[];
}

export default function agentBase<T extends ZodTypeAny>({
  name,
  role,
  instructions,
  knowledgeTopics = [],
  tools = [],
  model,
  classificationModel,
  responseSchema,
  contexts = [],
}: AgentBaseCreatorParams<T>): AgentBase<T> {
  const hasManualKnowledgeRagTool = tools.some((tool) => tool.name === ToolName.AskKnowledgeRAG);

  if (hasManualKnowledgeRagTool) {
    console.warn(
      `${ToolName.AskKnowledgeRAG} tool must not be added manually. Use the 'knowledgeTopics' field instead. Filtering out manually added ${ToolName.AskKnowledgeRAG} tools.`,
    );
  }

  const agentTools = tools.filter((tool) => tool.name !== ToolName.AskKnowledgeRAG);

  if (knowledgeTopics.length > 0) {
    agentTools.push(askKnowledgeRAGTool({knowledgeTopics: knowledgeTopics}));
  }

  const toolNames = agentTools.map((tool) => tool.name);
  _verifyNoDuplicates(toolNames);

  return {
    name,
    role,
    instructions: instructions ?? '',
    knowledgeTopics,
    tools: agentTools,
    responseSchema,
    model,
    classificationModel,
    contexts,
  };
}

function _verifyNoDuplicates(toolNames: string[]): void {
  const duplicates = toolNames.filter((name, index) => toolNames.indexOf(name) !== index);

  if (duplicates.length > 0) {
    throw new Error(`Duplicate tool names found: ${duplicates.join(', ')}`);
  }
}
